// import DocumentPicker from "expo-document-picker";
import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  setupPortalContainers,
  teardownPortalContainers,
  getDropdownPortalTarget,
} from "../utils/portalContainers"; // Add toast import
import "../assets/css/theme-table-header.css";
import { getUserData } from "./const";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getActiveItems } from "../utils/statusUtils";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import OtherAmountModal from "../components/OtherAmountModal";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import { createSingleStock, listSuppliers } from "../erpClient";
import {
  numPool,
  productGroupOptionDisplayName,
} from "../utils/productGroupPool";
import {
  searchProductGroupSelectOptions,
  enrichProductGroupSelectOption,
  mergeProductGroupOptions,
} from "../utils/productGroupOptions";
import { trackEvent, getTenantName } from "../services/analytics";
import { mapOtherAmountEntriesForCreate } from "../utils/otherAmountCalculations";
import {
  loadOtherChargeOptions,
  applyOtherChargeToEntry,
} from "../utils/otherChargeOptions";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";

/** True if the field is empty for required-row validation (numeric 0 is allowed). */
function isBlankTableField(v) {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return v.trim() === "";
  return false;
}

/** Resolve product group id from label/short_code/numeric id and dropdown list. */
function resolveProductGroupId(rawLabel, groups) {
  if (rawLabel === undefined || rawLabel === null) return null;
  const s = String(rawLabel).trim();
  if (!s) return null;
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 0 && String(asNum) === s) {
    return asNum;
  }
  if (!Array.isArray(groups) || groups.length === 0) return null;
  const lower = s.toLowerCase();
  const found = groups.find((g) => {
    const label = String(g.label ?? "").trim();
    const sc = String(g.short_code ?? "")
      .trim()
      .toLowerCase();
    return (
      label === s ||
      label.toLowerCase() === lower ||
      sc === lower ||
      String(g.value) === s ||
      Number(g.value) === asNum
    );
  });
  if (!found) return null;
  const id = Number(found.value);
  return Number.isFinite(id) ? id : null;
}

/** Resolve supplier id from label/short_code/numeric id and dropdown list. */
function resolveSupplierId(rawLabel, suppliers) {
  if (rawLabel === undefined || rawLabel === null) return null;
  const s = String(rawLabel).trim();
  if (!s) return null;
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 0 && String(asNum) === s) {
    return asNum;
  }
  if (!Array.isArray(suppliers) || suppliers.length === 0) return null;
  const lower = s.toLowerCase();
  const found = suppliers.find((sup) => {
    const label = String(sup.label ?? "").trim();
    const sc = String(sup.short_code ?? "")
      .trim()
      .toLowerCase();
    return (
      label === s ||
      label.toLowerCase() === lower ||
      sc === lower ||
      String(sup.value) === s ||
      Number(sup.value) === asNum
    );
  });
  if (!found) return null;
  const id = Number(found.value);
  return Number.isFinite(id) ? id : null;
}

const AddStock = ({
  masterGoldRate,
  masterSilverRate,
  showLiveRate = false,
}) => {
  const navigate = useNavigate();
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [canAddProduct, setCanAddProduct] = useState(() =>
    hasPermission(PERMISSIONS.PRODUCT_ADD)
  );

  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setCanAddProduct(hasPermission(PERMISSIONS.PRODUCT_ADD));
    };
    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);
    window.addEventListener("erpAuthUpdated", handlePermissionsUpdated);
    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
      window.removeEventListener("erpAuthUpdated", handlePermissionsUpdated);
    };
  }, []);
  const calculatePerGramRate = (rate, metalType) => {
    if (!showLiveRate) return rate;
    if (metalType === "Gold") return rate / 10;
    if (metalType === "Silver") return rate / 1000;
    return rate;
  };

  // Calculate per-gram rate adjusted by purity and metal
  const calculatePurityBasedRate = (purity, baseRate, metalType) => {
    if (!showLiveRate) return baseRate;
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return calculatePerGramRate(baseRate, metalType);

    if (metalType === "Gold") {
      // masterGoldRate is per 10g → per gram then multiply by (karat/24)
      const perGram24k = calculatePerGramRate(baseRate, metalType);
      return perGram24k * (purityValue / 24);
    }
    if (metalType === "Silver") {
      // 999 basis with same unit behavior as current price mode.
      return showLiveRate
        ? (baseRate * purityValue) / 999 / 1000
        : (baseRate * purityValue) / 999;
    }
    return calculatePerGramRate(baseRate, metalType);
  };
  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "tagNo",
      label: "Tag No",
      width: "75px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.tagNo}
          onChange={(e) => onChange(row.id, "tagNo", e.target.value)}
        />
      ),
    },
    {
      id: "productGroup",
      label: "Product Group",
      width: "160px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },

    {
      id: "design",
      label: "Design",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.design}
          onChange={(e) => onChange(row.id, "design", e.target.value)}
        />
      ),
    },
    {
      id: "size",
      label: "Size",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.size}
          onChange={(e) => onChange(row.id, "size", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: "GWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWtVepari",
      label: "LWt from Vepari",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWtVepari}
          onChange={(e) => onChange(row.id, "lsWtVepari", e.target.value)}
        />
      ),
    },
    {
      id: "lsWtCustomer",
      label: "LWt for Customer",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWtCustomer}
          onChange={(e) => onChange(row.id, "lsWtCustomer", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "NWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "lType",
      label: "Lbr Type",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.lType}
          onChange={(e) => onChange(row.id, "lType", e.target.value)}
        />
      ),
    },
    {
      id: "lbrPercentage",
      label: "Lbr %",
      width: "70px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrPercentage}
          onChange={(e) => onChange(row.id, "lbrPercentage", e.target.value)}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "Lbr Rate",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
        />
      ),
    },
    {
      id: "lbrAmt",
      label: "Lbr Amt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrAmt}
          onChange={(e) => onChange(row.id, "lbrAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "OthAmt",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "totalAmt",
      label: "Total Amt",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.totalAmt}
          onChange={(e) => onChange(row.id, "totalAmt", e.target.value)}
        />
      ),
    },
    {
      id: "mrp",
      label: "MRP",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.mrp}
          onChange={(e) => onChange(row.id, "mrp", e.target.value)}
        />
      ),
    },
    {
      id: "huidNo",
      label: "HUID",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.huidNo}
          onChange={(e) => onChange(row.id, "huidNo", e.target.value)}
        />
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onChange(row.id, "image", e.target.files[0])}
        />
      ),
    },
    {
      id: "supplier",
      label: "Supplier",
      width: "150px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.supplier}
          onChange={(e) => onChange(row.id, "supplier", e.target.value)}
        />
      ),
    },
  ];
  const tableMinWidth = COLUMNS.reduce(
    (total, column) => total + parseInt(column.width, 10),
    0,
  );

  // Search product groups via API when user types 3+ characters (no metal groups here)
  const fetchProductGroupsByPrefix = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      return [];
    }

    if (lastSearchedProductGroupPrefix === trimmed) {
      setProductGroupApiCalled(true);
      setProductGroupSearchLoading(false);
      return dropdownData.productGroup;
    }

    setProductGroupSearchLoading(true);
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      setDropdownData((prev) => ({
        ...prev,
        productGroup: opts,
      }));
      setLastSearchedProductGroupPrefix(trimmed);
      setProductGroupApiCalled(true);
      return opts;
    } catch (error) {
      console.error("Error searching product groups:", error);
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      setProductGroupApiCalled(true);
      return [];
    } finally {
      setProductGroupSearchLoading(false);
    }
  };

  const fetchSuppliers = async (prefix = "") => {
    // Avoid duplicate API calls for the same prefix
    if (prefix && prefix.length >= 2 && lastSearchedSupplierPrefix === prefix) {
      return;
    }

    try {
      setSupplierSearchLoading(true);
      // Build request payload
      const requestPayload = {
        type: "show",
      };

      // Add search parameter if prefix has 2+ characters
      if (prefix && prefix.length >= 2) {
        requestPayload.search = prefix;
        setLastSearchedSupplierPrefix(prefix); // Track the searched prefix
      }
      const response = await listSuppliers({
        search: requestPayload.search || undefined,
      });
      let karigarData = response?.success
        ? extractListRowsFromResponse(response, ["suppliers"])
        : [];

      if (karigarData.length > 0) {
        // Filter active karigars
        const activeKarigars = getActiveItems(karigarData, {
          statusField: "status",
          activeValue: 1,
        });

        const fetchedSuppliers = activeKarigars.map((karigar) => ({
          value: karigar.id,
          label: karigar.company_name || karigar.name || "",
          short_code: karigar.short_code || "",
        }));

        setDropdownData((prev) => ({
          ...prev,
          supplier: fetchedSuppliers,
        }));
      } else {
        // Clear suppliers if no results
        setDropdownData((prev) => ({
          ...prev,
          supplier: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      // Clear suppliers on error
      setDropdownData((prev) => ({
        ...prev,
        supplier: [],
      }));
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  // Removed It Rate recalculation effect

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    design: [],
    supplier: [],
    lType: ["NWt", "GWt", "Pcs", "Fixed"], // Keeping this as it is since it's not from API
  });
  const [lastSearchedSupplierPrefix, setLastSearchedSupplierPrefix] =
    useState("");
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const [socket, setSocket] = useState(null);

  // Columns that should have dropdown functionality
  const dropdownColumns = ["productGroup", "supplier", "lType", "rateOn"];

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: "",
    // Add imageFile property for image column
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [hoveredNoRowIndex, setHoveredNoRowIndex] = useState(null);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    inputLeft: 0,
    inputRight: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const tooltipRefs = useRef({}); // Add refs for tooltips
  const tableContainerRef = useRef(null);

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  const activePgBalance = useMemo(() => {
    const balanceFromOption = (opt) => {
      if (!opt || typeof opt !== "object") return null;
      return {
        pcs: numPool(opt.pool_pcs),
        nwt: numPool(opt.pool_nwt),
      };
    };

    if (
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
      const searchText = cell?.value ? String(cell.value).toLowerCase() : "";
      const items = dropdownData.productGroup || [];
      const filtered = searchText
        ? items.filter((item) => {
            const labelMatch =
              item.label &&
              String(item.label).toLowerCase().includes(searchText);
            const shortCodeMatch =
              item.short_code &&
              String(item.short_code).toLowerCase().includes(searchText);
            return labelMatch || shortCodeMatch;
          })
        : items;
      const fromHighlight = balanceFromOption(filtered[highlightedIndex]);
      if (fromHighlight) return fromHighlight;
    }

    const rowIndex =
      activeRowIndex != null ? activeRowIndex : activeDropdown.row;
    if (rowIndex == null || rowIndex < 0 || rowIndex >= tableData.length) {
      return null;
    }
    const pgCell = tableData[rowIndex]?.data?.find(
      (c) => c.id === "productGroup",
    );
    if (!pgCell) return null;

    if (
      pgCell.productGroupId != null &&
      (pgCell.pool_pcs != null || pgCell.pool_nwt != null)
    ) {
      return balanceFromOption(pgCell);
    }

    const pgId =
      pgCell.productGroupId != null ? Number(pgCell.productGroupId) : null;
    let opt = null;
    if (Number.isFinite(pgId) && pgId > 0) {
      opt = dropdownData.productGroup.find((o) => Number(o.value) === pgId);
    }
    if (!opt && pgCell.value) {
      const val = String(pgCell.value).trim();
      opt = dropdownData.productGroup.find(
        (o) =>
          String(o.label) === val ||
          String(o.name || "") === val ||
          productGroupOptionDisplayName(o) === val,
      );
    }
    return balanceFromOption(opt);
  }, [
    activeRowIndex,
    activeDropdown,
    highlightedIndex,
    tableData,
    dropdownData.productGroup,
  ]);

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    zoomLevel: 1,
  });

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      wtPP: "",
      wtTotal: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "120px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  const [billDate, setBillDate] = useState("");
  const [chargeTypes, setChargeTypes] = useState([]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadOtherCharges = async () => {
      try {
        const options = await loadOtherChargeOptions();
        if (!cancelled) setChargeTypes(options);
      } catch (error) {
        console.error("Error fetching other charges:", error);
        if (!cancelled) {
          toast.error(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load other charges",
          );
        }
      }
    };
    loadOtherCharges();

    const handleApiUrlChange = () => {
      loadOtherCharges();
    };
    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => {
      cancelled = true;
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Recompute labour fields when live-rate mode changes.
  useEffect(() => {
    setTableData((prev) =>
      prev.map((row) => {
        const nextRow = { ...row, data: [...row.data] };
        const findCell = (id) => nextRow.data.find((cell) => cell.id === id);

        const lType = String(findCell("lType")?.value || "");
        const isFixedType = lType.trim().toLowerCase() === "fixed";
        if (isFixedType) return nextRow;

        const selectedProductGroup = dropdownData.productGroup.find(
          (pg) => pg.label === String(findCell("productGroup")?.value || ""),
        );
        const baseRate =
          selectedProductGroup?.metal === "Gold"
            ? masterGoldRate
            : masterSilverRate;
        const perGramRate = selectedProductGroup?.purity
          ? calculatePurityBasedRate(
              selectedProductGroup.purity,
              baseRate,
              selectedProductGroup?.metal,
            )
          : calculatePerGramRate(baseRate, selectedProductGroup?.metal);

        const lbrPercentageCell = findCell("lbrPercentage");
        const lbrRateCell = findCell("lbrRate");
        const lbrAmtCell = findCell("lbrAmt");
        const totalAmtCell = findCell("totalAmt");
        const othAmtCell = findCell("othAmt");
        let lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
        let lbrRate = parseFloat(lbrRateCell?.value) || 0;

        const calcMode =
          row.lbrCalcMode || (lbrPercentage > 0 ? "percent" : "rate");
        if (calcMode === "percent") {
          lbrRate = lbrPercentage > 0 ? (lbrPercentage / 100) * perGramRate : 0;
          if (lbrRateCell) lbrRateCell.value = lbrRate.toFixed(2);
        } else if (calcMode === "rate" && lbrPercentageCell) {
          // Direct rate entry: leave percentage blank (do not back-calculate)
          lbrPercentageCell.value = "";
          lbrPercentage = 0;
        }

        const gwValue = parseFloat(findCell("grWt")?.value) || 0;
        const lwValue = parseFloat(findCell("lsWtCustomer")?.value) || 0;
        const netWeight = calculateNetWeight(gwValue, lwValue);
        const pcsValue = parseFloat(findCell("pcs")?.value) || 0;
        const baseWeight =
          lType === "NWt"
            ? netWeight
            : lType === "GWt"
              ? gwValue
              : lType === "Pcs"
                ? pcsValue
                : 0;

        let lbrAmt = 0;
        if (lbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            perGramRate,
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }
        if (lbrAmtCell) lbrAmtCell.value = Math.round(lbrAmt).toString();

        if (totalAmtCell) {
          const othAmt = parseFloat(othAmtCell?.value) || 0;
          totalAmtCell.value = (Math.round(lbrAmt) + othAmt).toFixed(2);
        }

        return nextRow;
      }),
    );
  }, [
    showLiveRate,
    masterGoldRate,
    masterSilverRate,
    dropdownData.productGroup,
  ]);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Charge type suggestions come from the other charges master loaded on page load.
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = chargeTypes.filter((charge) =>
      (charge.codes || []).some((code) => code.toLowerCase() === searchQuery),
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = chargeTypes.filter((charge) =>
        (charge.codes || []).some((code) => code.toLowerCase().startsWith(searchQuery)),
      );
    }

    // Get matches by name
    const matchedByName = chargeTypes.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name),
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) =>
        charge.codes[0] ? `${charge.name} (${charge.codes[0]})` : charge.name,
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      // For existing entries
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      // For current entry
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  /** Viewport-fixed position for portal dropdowns (survives sidebar width changes). */
  const getAnchoredDropdownPosition = (inputElement, dropdownWidthHint) => {
    if (!inputElement) return null;
    const rect = inputElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const width = Math.max(rect.width, dropdownWidthHint || 0, 100);
    const edgePad = 8;
    // Prefer left-align under the input; flip/clamp when near the right edge
    // (Supplier is the last column and often overflows when the sidebar is open).
    let left = rect.left;
    if (left + width > viewportWidth - edgePad) {
      left = rect.right - width;
    }
    left = Math.min(
      Math.max(edgePad, left),
      Math.max(edgePad, viewportWidth - width - edgePad),
    );
    return {
      top: rect.bottom + 1,
      left,
      width: rect.width,
      inputLeft: rect.left,
      inputRight: rect.right,
    };
  };

  // Update dropdown position after API response when dropdown is active
  useEffect(() => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    ) {
      return;
    }
    if (
      activeDropdown.type !== "productGroup" &&
      activeDropdown.type !== "supplier"
    ) {
      return;
    }
    const inputElement =
      inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
    const next = getAnchoredDropdownPosition(inputElement);
    if (next) setDropdownPosition(next);
  }, [
    productGroupApiCalled,
    dropdownData.productGroup,
    dropdownData.supplier,
    activeDropdown,
  ]);

  // Keep fixed portal dropdowns anchored when sidebar/layout/table scroll changes
  useEffect(() => {
    if (activeDropdown.row === null || activeDropdown.col === null) return;

    const reposition = () => {
      const inputElement =
        inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
      const next = getAnchoredDropdownPosition(inputElement);
      if (next) setDropdownPosition(next);
    };

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [activeDropdown.row, activeDropdown.col]);

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container for dropdown
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Socket.IO listener for real-time updates
  // useEffect(() => {
  //   const newSocket = io("https://api.gurusoftware.cloud", {
  //     transports: ["websocket", "polling"],
  //   });
  //   setSocket(newSocket);

  //   // CORRECTED: Listen for 'product_created' for individual items
  //   newSocket.on("product_created", (data) => {
  //     console.log("Product created event received:", data);

  //     // Update your table/list state here if needed
  //     setProducts((prev) => {
  //       return [data, ...prev];
  //       // return [...prev, data];
  //     });

  //     if (data?.tag_no) {
  //       toast.success(`Product created: ${data.tag_no}`);
  //     }
  //   });

  //   // CORRECTED: Listen for 'stock_added' for the batch summary
  //   newSocket.on("stock_added", (data) => {
  //     console.log("Stock added event received:", data);
  //     if (data?.voucher_number && data?.count) {
  //       toast.success(
  //         `${data.count} products added successfully! Voucher: ${data.voucher_number}`
  //       );
  //     }
  //   });

  //   newSocket.on("connect", () => {
  //     console.log("Socket connected for AddStock");
  //   });

  //   return () => {
  //     newSocket.off("product_created");
  //     newSocket.off("stock_added");
  //     newSocket.disconnect();
  //   };
  // }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt for customer
  };

  /** NWt for submit: use entered netWt (0 allowed), else derive from GWt − LWt (customer). */
  const resolveSubmitNwt = (rowData) => {
    const rawNet = rowData.netWt;
    if (
      rawNet !== undefined &&
      rawNet !== null &&
      String(rawNet).trim() !== ""
    ) {
      const entered = parseFloat(rawNet);
      return Number.isFinite(entered) ? entered : 0;
    }
    const gw = parseFloat(rowData.grWt) || 0;
    const lw = parseFloat(rowData.lsWtCustomer) || 0;
    const computed = calculateNetWeight(gw, lw);
    return Number.isFinite(computed) ? computed : 0;
  };

  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    perGramRate,
  ) => {
    return (lbrPercentage / 100) * baseWeight * (parseFloat(perGramRate) || 0);
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (
    baseWeight,
    lbrRate,
    masterRate,
    metalType,
  ) => {
    // lbrRate is assumed to be per gram
    return lbrRate * baseWeight;
  };

  const calculateLbrAmt = (baseWeight, lbrPercentage, lbrRate, perGramRate) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        perGramRate,
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value, idOptions = {}) => {
    // Get the column type
    const columnType = COLUMNS[colIndex].id;

    // Validation rules for different fields
    const validations = {
      design: /^[a-zA-Z0-9\s-_]*$/, // Allow alphabets, numbers, spaces, hyphens and underscores
      size: /^[^\\]*$/, // Allow size symbols and free-form text
      pcs: /^\d*$/, // Only numbers
      grWt: /^\d*\.?\d*$/, // Numbers and dot
      lsWtVepari: /^\d*\.?\d*$/, // Numbers and dot
      lsWtCustomer: /^\d*\.?\d*$/, // Numbers and dot
      netWt: /^\d*\.?\d*$/, // Numbers and dot
      lbrPercentage: /^\d*\.?\d*$/, // Numbers and dot
      lbrRate: /^\d*\.?\d*$/, // Numbers and dot
      lbrAmt: /^\d*\.?\d*$/, // Numbers and dot
      othAmt: /^\d*\.?\d*$/, // Numbers and dot
      totalAmt: /^\d*\.?\d*$/, // Numbers and dot
      mrp: /^\d*\.?\d*$/, // Numbers and dot
    };

    // Check if this column has a validation rule
    if (validations[columnType]) {
      // If value doesn't match validation pattern and isn't empty, don't update
      if (value !== "" && !validations[columnType].test(value)) {
        return;
      }
    }

    setTableData((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[rowIndex] && updatedData[rowIndex].data) {
        const row = updatedData[rowIndex];
        // Get current lType value before updating
        const currentLType = row.data.find(
          (cell) => cell.id === "lType",
        )?.value;
        const isCurrentTypeFixed =
          String(currentLType || "")
            .trim()
            .toLowerCase() === "fixed";

        // Check if trying to update lbrPercentage or lbrRate when lType is "Fixed"
        if (
          isCurrentTypeFixed &&
          (columnType === "lbrPercentage" || columnType === "lbrRate")
        ) {
          // Don't allow updating these fields when lType is "Fixed"
          return updatedData;
        }

        // Check if trying to update lbrAmt when lType is NOT "Fixed"
        if (!isCurrentTypeFixed && columnType === "lbrAmt") {
          // Don't allow updating lbrAmt when lType is not "Fixed"
          return updatedData;
        }

        // Proceed with the update for other cases
        const cell = row.data[colIndex];
        cell.value = value;
        if (idOptions.setProductGroupId != null) {
          cell.productGroupId = idOptions.setProductGroupId;
          if (idOptions.poolPcs != null) cell.pool_pcs = idOptions.poolPcs;
          if (idOptions.poolNwt != null) cell.pool_nwt = idOptions.poolNwt;
        } else if (columnType === "productGroup") {
          delete cell.productGroupId;
          delete cell.pool_pcs;
          delete cell.pool_nwt;
        }
        if (idOptions.setSupplierId != null) {
          cell.supplierId = idOptions.setSupplierId;
        } else if (columnType === "supplier") {
          delete cell.supplierId;
        }

        // Track whether user intends % or rate for API lbr_type
        if (columnType === "lbrPercentage") {
          row.lbrCalcMode = "percent";
        } else if (columnType === "lbrRate") {
          row.lbrCalcMode = "rate";
        } else if (
          columnType === "lType" &&
          String(value || "")
            .trim()
            .toLowerCase() === "fixed"
        ) {
          row.lbrCalcMode = "flat";
        }

        // If we're changing the lType to "Fixed", clear lbrPercentage and lbrRate fields
        if (
          columnType === "lType" &&
          String(value || "")
            .trim()
            .toLowerCase() === "fixed"
        ) {
          const lbrPercentageCell = row.data.find(
            (cell) => cell.id === "lbrPercentage",
          );
          if (lbrPercentageCell) {
            lbrPercentageCell.value = "";
          }

          const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
          if (lbrRateCell) {
            lbrRateCell.value = "";
          }
        }

        // Calculate GWt and LWt for customer
        const gwValue =
          parseFloat(row.data.find((cell) => cell.id === "grWt")?.value) || 0;
        const lwValue =
          parseFloat(
            row.data.find((cell) => cell.id === "lsWtCustomer")?.value,
          ) || 0;

        // Calculate NWt based on GWt and LWt for customer
        const netWeight = calculateNetWeight(gwValue, lwValue);

        if (columnType === "grWt" || columnType === "lsWtCustomer") {
          // Update NWt when GWt or LWt for customer changes
          const netWtCell = row.data.find((cell) => cell.id === "netWt");
          if (netWtCell) {
            netWtCell.value = netWeight.toFixed(2); // Update NWt with 2 decimal places
          }
        }

        // Calculate Lbr Amt based on Lbr Type
        const lbrType = row.data.find((cell) => cell.id === "lType")?.value;
        const isLbrTypeFixed =
          String(lbrType || "")
            .trim()
            .toLowerCase() === "fixed";
        const lbrPercentage =
          parseFloat(
            row.data.find((cell) => cell.id === "lbrPercentage")?.value,
          ) || 0;
        const lbrRate =
          parseFloat(row.data.find((cell) => cell.id === "lbrRate")?.value) ||
          0;
        let baseWeight = 0;

        // Determine Base Weight based on Lbr Type
        if (lbrType === "NWt") {
          baseWeight = netWeight;
        } else if (lbrType === "GWt") {
          baseWeight = gwValue;
        } else if (lbrType === "Pcs") {
          baseWeight =
            parseFloat(row.data.find((cell) => cell.id === "pcs")?.value) || 0;
        }

        // Get product group and metal type for per-gram rate
        const productGroupCell = updatedData[rowIndex].data.find(
          (cell) => cell.id === "productGroup",
        );
        const selectedProductGroup =
          idOptions.selectedProductGroup ||
          dropdownData.productGroup.find(
            (pg) => pg.label === productGroupCell?.value,
          );
        const metalType = selectedProductGroup?.metal || "Gold"; // Default to Gold if not found
        const baseRate =
          selectedProductGroup?.metal === "Gold"
            ? masterGoldRate
            : masterSilverRate;
        const perGramRate = selectedProductGroup?.purity
          ? calculatePurityBasedRate(
              selectedProductGroup.purity,
              baseRate,
              selectedProductGroup?.metal,
            )
          : calculatePerGramRate(baseRate, selectedProductGroup?.metal);

        // Sync lbrRate and lbrPercentage fields
        let effectiveLbrPercentage = lbrPercentage;
        let effectiveLbrRate = lbrRate;
        if (columnType === "lbrPercentage" && !isLbrTypeFixed) {
          const lbrRateCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "lbrRate",
          );
          effectiveLbrPercentage = parseFloat(value) || 0;
          effectiveLbrRate =
            effectiveLbrPercentage > 0
              ? (effectiveLbrPercentage / 100) * perGramRate
              : 0;
          if (lbrRateCell) {
            lbrRateCell.value = effectiveLbrRate.toFixed(2);
          }
        }
        if (columnType === "lbrRate" && !isLbrTypeFixed) {
          // Direct rate entry for NWt/GWt/Pcs: leave percentage blank
          const lbrPercentageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "lbrPercentage",
          );
          if (lbrPercentageCell) {
            lbrPercentageCell.value = "";
          }
          effectiveLbrPercentage = 0;
          effectiveLbrRate = parseFloat(value) || 0;
        }

        // Calculate Lbr Amt
        let lbrAmt = 0;
        if (effectiveLbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            effectiveLbrPercentage,
            perGramRate,
          );
        } else if (effectiveLbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, effectiveLbrRate);
        }

        // Update Lbr Amt only for non-Fixed types.
        // For Fixed, user-entered lbrAmt must be preserved.
        const lbrAmtCell = updatedData[rowIndex].data.find(
          (cell) => cell.id === "lbrAmt",
        );
        if (lbrAmtCell && !isLbrTypeFixed) {
          // Round to the nearest whole number for display
          lbrAmtCell.value = Math.round(lbrAmt).toString();
        }

        // Calculate Total Amt when any amount field changes
        if (
          columnType === "lbrAmt" ||
          columnType === "othAmt" ||
          columnType === "netWt" ||
          columnType === "lbrPercentage" ||
          columnType === "lbrRate" ||
          columnType === "lType" ||
          columnType === "grWt" ||
          columnType === "lsWtVepari" ||
          columnType === "pcs"
        ) {
          const lbrAmt =
            parseFloat(
              updatedData[rowIndex].data.find((cell) => cell.id === "lbrAmt")
                ?.value,
            ) || 0;
          const othAmt =
            parseFloat(
              updatedData[rowIndex].data.find((cell) => cell.id === "othAmt")
                ?.value,
            ) || 0;

          const totalAmt = lbrAmt + othAmt;
          const totalAmtCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "totalAmt",
          );
          if (totalAmtCell) {
            totalAmtCell.value = totalAmt.toFixed(2);
          }
        }
      }
      return updatedData;
    });

    // Handle supplier field: fetch options when user types 2+ characters
    if (columnType === "supplier") {
      // Trigger API call when user types 2+ characters
      if (value && value.length >= 2) {
        // Only fetch if the new value doesn't start with the last searched prefix
        if (
          !lastSearchedSupplierPrefix ||
          !value.startsWith(lastSearchedSupplierPrefix)
        ) {
          fetchSuppliers(value);
        }
      } else if (!value || value.length === 0) {
        // Clear suppliers when field is cleared
        setDropdownData((prev) => ({
          ...prev,
          supplier: [],
        }));
        setLastSearchedSupplierPrefix(""); // Reset prefix tracking
        setSupplierSearchLoading(false);
      }
    }
    if (columnType === "productGroup" && idOptions.setProductGroupId == null) {
      if (value && value.length >= 3) {
        if (
          !lastSearchedProductGroupPrefix ||
          value !== lastSearchedProductGroupPrefix
        ) {
          setProductGroupApiCalled(false);
          fetchProductGroupsByPrefix(value);
        }
      } else {
        setDropdownData((prev) => ({ ...prev, productGroup: [] }));
        setLastSearchedProductGroupPrefix("");
        setProductGroupApiCalled(false);
        setProductGroupSearchLoading(false);
      }
    }

    // Check if this is the last column of the current row and it's the last row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      // Automatically add a new row when cursor is in the last column of the last row
      handleAddRow();
    }
  };

  const [imageName, setImageName] = useState("");

  // Open image modal
  const openImageModal = (imageUrl) => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    setImageModal({
      isOpen: true,
      imageUrl,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal
        show={imageModal.isOpen}
        onHide={closeImageModal}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                // Trigger file input to change the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl,
                  ),
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image",
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                // Find and remove the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl,
                  ),
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image",
                  );
                  if (colIndex !== -1) {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      const imageCell = updatedData[rowIndex].data.find(
                        (c) => c.id === "image",
                      );
                      if (imageCell) {
                        imageCell.value = "";
                        imageCell.imageFile = null;
                        imageCell.imagePreview = null;
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal"),
    );
  };

  const handleAddRow = () => {
    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: "",
          // Add imageFile property for image column
          ...(col.id === "image"
            ? { imageFile: null, imagePreview: null }
            : {}),
        })),
      },
    ]);
    // Keep new row + Total in view (same as Sales): older rows scroll up
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop =
          tableContainerRef.current.scrollHeight;
      }
    }, 150);
  };

  const handleDuplicateRow = (sourceRowIndex) => {
    setTableData((prevData) => {
      if (!prevData.length) return prevData;
      if (sourceRowIndex < 0 || sourceRowIndex >= prevData.length)
        return prevData;
      const targetRowIndex =
        activeRowIndex != null &&
        activeRowIndex >= 0 &&
        activeRowIndex < prevData.length
          ? activeRowIndex
          : sourceRowIndex;

      const sourceRow = prevData[sourceRowIndex];
      const copiedRowData = sourceRow.data.map((cell) => ({
        ...cell,
        ...(cell.id === "image"
          ? {
              imageFile: cell.imageFile || null,
              imagePreview: cell.imagePreview || null,
            }
          : {}),
        ...(cell.id === "othAmt"
          ? {
              otherAmountEntries: Array.isArray(cell.otherAmountEntries)
                ? cell.otherAmountEntries.map((entry) => ({ ...entry }))
                : [],
            }
          : {}),
      }));

      return prevData.map((row, idx) =>
        idx === targetRowIndex
          ? {
              ...row,
              data: copiedRowData,
            }
          : row,
      );
    });
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    const next = getAnchoredDropdownPosition(inputElement, 200);
    if (next) setDropdownPosition(next);

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setActiveRowIndex(rowIndex);
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = async (rowIndex, colIndex, selectedItem) => {
    const columnId = COLUMNS[colIndex]?.id;
    let item = selectedItem;

    // Load metal group (purity/metal) only after a product group is chosen
    if (
      columnId === "productGroup" &&
      typeof selectedItem === "object" &&
      selectedItem?.value
    ) {
      try {
        const enriched = await enrichProductGroupSelectOption(selectedItem);
        if (enriched) {
          item = enriched;
          setDropdownData((prev) => ({
            ...prev,
            productGroup: mergeProductGroupOptions(prev.productGroup, [
              enriched,
            ]),
          }));
        }
      } catch (error) {
        console.error("Error enriching product group with metal:", error);
      }
    }

    // Ensure we're passing the label or value, not the entire object
    const value = typeof item === "object" ? item.label : item;

    const idOpts =
      typeof item === "object" && item
        ? columnId === "productGroup"
          ? {
              setProductGroupId: item.value,
              poolPcs: item.pool_pcs,
              poolNwt: item.pool_nwt,
              selectedProductGroup: item,
            }
          : columnId === "supplier"
            ? { setSupplierId: item.value }
            : {}
        : {};

    handleInputChange(rowIndex, colIndex, value, idOpts);
    setActiveDropdown({ row: null, col: null, type: null });

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Ensure searchText is a string
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    if (!normalizedSearchText) return dropdownData[columnType];

    // Special handling for lType (which is an array of strings)
    if (columnType === "lType") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText),
      );
    }

    // Special handling for productGroup (search both label and short_code)
    if (columnType === "productGroup") {
      return dropdownData[columnType].filter((item) => {
        const labelMatch =
          (item.label &&
            String(item.label).toLowerCase().includes(normalizedSearchText)) ||
          false;
        const shortCodeMatch =
          (item.short_code &&
            String(item.short_code)
              .toLowerCase()
              .includes(normalizedSearchText)) ||
          false;
        return labelMatch || shortCodeMatch;
      });
    }

    // Special handling for supplier (search both label and short_code)
    if (columnType === "supplier") {
      return dropdownData[columnType].filter((item) => {
        const labelMatch =
          item.label?.toLowerCase().includes(normalizedSearchText) || false;
        const shortCodeMatch =
          item.short_code?.toLowerCase().includes(normalizedSearchText) ||
          false;
        return labelMatch || shortCodeMatch;
      });
    }

    // For other dropdown types
    return dropdownData[columnType].filter((item) => {
      // Safely handle both object and string types
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return item.toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus new row, then keep Total in view (same as Sales)
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
          if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop =
              tableContainerRef.current.scrollHeight;
          }
        }, 150);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex],
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const searchText = cell.value ? String(cell.value).trim() : "";
    const isProductGroup = activeDropdown.type === "productGroup";
    const isSupplier = activeDropdown.type === "supplier";

    // Don't show search dropdowns until the user has typed the minimum characters
    if (isProductGroup && searchText.length < 3) return null;
    if (isSupplier && searchText.length < 2) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);

    // Reset refs array
    dropdownItemsRef.current = [];

    // Special styling for productGroup dropdown - match Sales.jsx pattern
    // For productGroup: use 5 items with 40px height each (matching Sales.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = isProductGroup ? 40 : 35;
    const listLen =
      (isProductGroup && productGroupSearchLoading) ||
      (isSupplier && supplierSearchLoading)
        ? 1
        : Math.max(filtered.length, 1);
    const visibleItems = Math.min(listLen, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    // Width: productGroup skips DOM measuring (hundreds of items would freeze UI)
    let adjustedWidth;
    if (isProductGroup) {
      adjustedWidth = Math.max(200, dropdownPosition.width);
    } else if (!filtered.length) {
      // Keep empty-state message readable (avoid clipped "No matching items")
      adjustedWidth = Math.max(dropdownPosition.width || 0, 180);
    } else {
      const maxContentWidth = Math.max(
        ...filtered.map((item) => {
          const tempSpan = document.createElement("span");
          tempSpan.style.fontSize = "0.85rem";
          tempSpan.style.visibility = "hidden";
          tempSpan.style.position = "absolute";
          tempSpan.style.whiteSpace = "nowrap";
          const itemLabel = typeof item === "object" ? item.label || "" : item;
          tempSpan.textContent = itemLabel;
          document.body.appendChild(tempSpan);
          const width = tempSpan.offsetWidth;
          document.body.removeChild(tempSpan);
          return width;
        }),
        dropdownPosition.width,
      );
      adjustedWidth = Math.max(maxContentWidth + 10, 100);
    }

    // Re-clamp with the final width so last-column dropdowns (Supplier) stay on-screen
    // when the sidebar reduces the available viewport.
    const viewportWidth = window.innerWidth;
    const edgePad = 8;
    const inputLeft = dropdownPosition.inputLeft ?? dropdownPosition.left;
    const inputRight =
      dropdownPosition.inputRight ?? inputLeft + dropdownPosition.width;
    let anchoredLeft = inputLeft;
    if (anchoredLeft + adjustedWidth > viewportWidth - edgePad) {
      anchoredLeft = inputRight - adjustedWidth;
    }
    anchoredLeft = Math.min(
      Math.max(edgePad, anchoredLeft),
      Math.max(edgePad, viewportWidth - adjustedWidth - edgePad),
    );

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "fixed",
          top: `${dropdownPosition.top}px`,
          left: `${anchoredLeft}px`,
          width: `${adjustedWidth}px`,
          minWidth: `${adjustedWidth}px`,
          maxHeight: isProductGroup
            ? `${dropdownHeight + 16}px`
            : `${dropdownHeight}px`,
          height: isProductGroup
            ? `${dropdownHeight + 16}px`
            : `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {isProductGroup && productGroupSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : isSupplier && supplierSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : filtered && filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? productGroupOptionDisplayName(item)
                    : item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                    (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={item?.value ?? idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${
                    idx === highlightedIndex ? "active" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    padding: isProductGroup ? "0.4rem 0.75rem" : "0.25rem 1rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: isProductGroup ? "normal" : "nowrap",
                    wordWrap: isProductGroup ? "break-word" : "normal",
                    overflow: isProductGroup ? "visible" : "hidden",
                    textOverflow: isProductGroup ? "clip" : "ellipsis",
                  }}
                  title={itemLabel}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item,
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : (
            <div className="dropdown-item disabled">No matching items</div>
          )}
        </div>
      </div>,
      getDropdownPortalTarget(),
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    setActiveRowIndex(rowIndex);
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    // If this is a dropdown column, show the dropdown
    const columnType = getColumnType(colIndex);
    if (isDropdownColumn(columnType)) {
      if (columnType === "productGroup") {
        const productGroupCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "productGroup",
        );
        const productGroupValue = (productGroupCell?.value || "")
          .toString()
          .trim();
        if (productGroupValue.length >= 3) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
      } else if (columnType === "supplier") {
        const supplierCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "supplier",
        );
        const supplierValue = (supplierCell?.value || "").toString().trim();
        // Suppliers load only after 2+ characters — don't show empty "No matching"
        if (supplierValue.length >= 2) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
      } else {
        // For other dropdown columns, show dropdown normally
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  // Add helper function to open modal
  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt")
        .otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt").value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => {
            const wtPP = entry.wtPP ?? entry.wt ?? "";
            // XOR UI: show PP path from legacy `wt`; do not duplicate total in the other field.
            return {
              ...entry,
              rateOn: entry.rateOn || "", // Ensure rateOn is initialized
              wtPP,
              wtTotal: entry.wtTotal ?? "",
              wt: wtPP, // keep internal calculation using PP in `wt`
            };
          })
        : currentValue
          ? [
              {
                chargeType: "Direct Entry",
                pcs: "",
                ct: "",
                wt: "",
                wtPP: "",
                wtTotal: "",
                conPercentage: "",
                rateOn: "",
                rate: "",
                amount: currentValue,
              },
            ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        wtPP: "",
        wtTotal: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  // Add function to save other amount entries
  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    // If there's data in the current entry, validate and add it to entries
    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    // Calculate total amount
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );

    // Calculate total less weight
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry); // Assuming you have a function to calculate less weight
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    // Update table data with both values
    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      // Get current lType value and lbrAmt before updating
      const lTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
      const currentLType = lTypeCell?.value;
      const currentLbrAmt = lbrAmtCell?.value || "0";

      // Update Other Amount (rounded)
      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount); // Ensure this is formatted correctly
        othAmtCell.otherAmountEntries = finalEntries; // Store the entries
      }

      // Sync LWt (Customer) from Other Amount entries immediately
      const lsWtCustomerCell = row.data.find(
        (cell) => cell.id === "lsWtCustomer",
      );
      if (lsWtCustomerCell) {
        lsWtCustomerCell.value = formatLessWeight(totalLessWeight);
      }

      // Calculate and update NWt after LWt is set
      const gwValue = parseFloat(
        row.data.find((cell) => cell.id === "grWt")?.value || 0,
      );
      // Use LWt for customer to calculate NWt
      const lwValue = parseFloat(
        row.data.find((cell) => cell.id === "lsWtCustomer")?.value || 0,
      );
      const netWeight = calculateNetWeight(gwValue, lwValue);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2); // Update NWt with 2 decimal places
      }

      // Only calculate and update lbrAmt if lType is NOT "Fixed"
      // Otherwise, preserve the manually entered value
      const lbrType = lTypeCell?.value;
      const isLbrTypeFixed =
        String(lbrType || "")
          .trim()
          .toLowerCase() === "fixed";
      if (!isLbrTypeFixed) {
        const lbrPercentage =
          parseFloat(
            row.data.find((cell) => cell.id === "lbrPercentage")?.value,
          ) || 0;
        const lbrRate =
          parseFloat(row.data.find((cell) => cell.id === "lbrRate")?.value) ||
          0;
        const baseWeight = lbrType === "NWt" ? netWeight : gwValue; // Adjust base weight based on Lbr Type

        const productGroupCell = row.data.find(
          (cell) => cell.id === "productGroup",
        );
        const selectedProductGroup = dropdownData.productGroup.find(
          (pg) => pg.label === productGroupCell?.value,
        );
        const baseRate =
          selectedProductGroup?.metal === "Gold"
            ? masterGoldRate
            : masterSilverRate;
        const perGramRate = selectedProductGroup?.purity
          ? calculatePurityBasedRate(
              selectedProductGroup.purity,
              baseRate,
              selectedProductGroup?.metal,
            )
          : calculatePerGramRate(baseRate, selectedProductGroup?.metal);

        const lbrAmt = calculateLbrAmt(
          baseWeight,
          lbrPercentage,
          lbrRate,
          perGramRate,
        );
        if (lbrAmtCell) {
          lbrAmtCell.value = lbrAmt.toFixed(2); // Update Lbr Amt with 2 decimal places
        }
      } else if (lbrAmtCell) {
        // If lType is "Fixed", make sure we preserve the lbrAmt value
        lbrAmtCell.value = currentLbrAmt;
      }

      // Keep row total in sync after OthAmt modal save.
      const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
      if (totalAmtCell) {
        const lbrAmtValue = parseFloat(lbrAmtCell?.value || 0) || 0;
        const othAmtValue = parseFloat(othAmtCell?.value || 0) || 0;
        totalAmtCell.value = (lbrAmtValue + othAmtValue).toFixed(2);
      }

      return newData;
    });

    // Close modal after saving
    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Add function to handle column width changes
  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase()),
    );

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry,
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        // Close dropdown
        const dropdown = e.target.nextElementSibling;
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };

  // Add calculation functions before the Table component
  const calculateTotalCt = (entry) => {
    const ct = parseFloat(entry.ct);

    if (isNaN(ct)) return 0;
    return ct;
  };

  /** Total weight for "WT" rate: either explicit Total (wtTotal) XOR per-piece (wtPP/wt) × Pcs. */
  const calculateTotalWt = (entry) => {
    const tStr = entry.wtTotal;
    const hasTotal =
      tStr !== undefined &&
      tStr !== null &&
      String(tStr).trim() !== "" &&
      Number.isFinite(parseFloat(tStr));

    if (hasTotal) {
      const t = parseFloat(tStr);
      return Number.isFinite(t) ? t : 0;
    }

    const pcs = parseFloat(entry.pcs);
    const wt = parseFloat(entry.wtPP ?? entry.wt);
    if (isNaN(pcs) || isNaN(wt)) return 0;
    return pcs * wt;
  };

  const calculateLessWeight = (entry) => {
    // Calculate total weight first
    const totalWt = calculateTotalWt(entry);
    if (!totalWt || isNaN(totalWt)) return 0;

    // Get concentration percentage
    const conPercentage = parseFloat(entry.conPercentage);

    // If there's a valid concentration percentage, calculate less weight based on that
    if (!isNaN(conPercentage) && conPercentage > 0) {
      return totalWt * (conPercentage / 100);
    }

    // If no concentration percentage, return the total weight
    return totalWt;
  };

  const calculateAmount = (entry) => {
    if (!entry.rate || isNaN(entry.rate)) return 0;

    const rate = parseFloat(entry.rate);
    const rateOn = entry.rateOn?.toLowerCase() || "";
    let amount = 0;

    switch (rateOn) {
      case "fixed":
        amount = rate;
        break;
      case "pcs":
        amount =
          entry.pcs && !isNaN(entry.pcs) ? rate * parseFloat(entry.pcs) : 0;
        break;
      case "ct":
        const totalCt = calculateTotalCt(entry);
        amount = totalCt ? rate * totalCt : 0;
        break;
      case "wt":
        const totalWt = calculateTotalWt(entry);
        amount = totalWt ? rate * totalWt : 0;
        break;
      default:
        amount = 0;
    }

    // Return the formatted amount
    return amount;
  };

  const calculateTotal = (entries) => {
    return entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
  };

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Define product and setProductGroups using useState

  const [design, setDesign] = useState(""); // Initialize design state
  const [size, setSize] = useState(""); // Initialize size state
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [lType, setLType] = useState(""); // Initialize lType state
  const [lbrPercentage, setLbrPercentage] = useState(0); // Initialize lbrPercentage state
  const [lbrRate, setLbrRate] = useState(0); // Initialize lbrRate state
  const [lbrAmt, setLbrAmt] = useState(0); // Initialize lbramt state
  const [othamt, setOthAmt] = useState(0); // Initialize othAmt state
  const [mrp, setMrp] = useState(0); // Initialize mrp state
  const [image, setImage] = useState(null); // Initialize image state
  const [supplier, setSupplier] = useState(""); // Initialize supplier state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [productGroup, setProductGroup] = useState(""); // Initialize productGroup state (assuming it's a string)
  const [products, setProducts] = useState([]); // State to hold the list of products
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [lastHuidEnterPress, setLastHuidEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });
  const [huidModal, setHuidModal] = useState({ isOpen: false, rowIndex: null });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Log available suppliers for debugging
    // console.log("Available suppliers:", dropdownData.supplier);
    // console.log("Available product groups:", dropdownData.productGroup);

    // Create an array to hold all product entries
    const productEntries = [];

    // Process each row in the table
    for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
      const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
        acc[cell.id] = cell.value;
        return acc;
      }, {});

      // Skip rows with empty required fields (design and supplier are optional)
      if (isBlankTableField(rowData.productGroup)) {
        continue;
      }

      const productGroupCell = tableData[rowIndex].data.find(
        (c) => c.id === "productGroup",
      );
      const supplierCell = tableData[rowIndex].data.find(
        (c) => c.id === "supplier",
      );

      let resolvedProductGroupId =
        productGroupCell?.productGroupId != null
          ? Number(productGroupCell.productGroupId)
          : null;
      if (!Number.isFinite(resolvedProductGroupId)) {
        resolvedProductGroupId = resolveProductGroupId(
          rowData.productGroup,
          dropdownData.productGroup,
        );
      }

      let resolvedSupplierId =
        supplierCell?.supplierId != null
          ? Number(supplierCell.supplierId)
          : null;
      if (!Number.isFinite(resolvedSupplierId)) {
        resolvedSupplierId = resolveSupplierId(
          rowData.supplier,
          dropdownData.supplier,
        );
      }

      if (resolvedProductGroupId == null) {
        toast.error(
          `Row ${rowIndex + 1}: choose Product Group from the dropdown so the ID is saved, or check spelling matches the list.`,
        );
        return;
      }

      const resolvedNwt = resolveSubmitNwt(rowData);

      // Get other amount entries (charges)
      const otherAmtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "othAmt",
      );
      const otherAmountEntries = otherAmtCell?.otherAmountEntries || [];

      const charges = mapOtherAmountEntriesForCreate(otherAmountEntries);

      // Get LBR amount
      let lbrAmount = 0;
      const lbrAmtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "lbrAmt",
      );
      if (lbrAmtCell && lbrAmtCell.value) {
        lbrAmount = Number(lbrAmtCell.value) || 0;
      }

      // Find the image file for this row
      const imageCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "image",
      );
      let imageName = "";
      if (imageCell && imageCell.imageFile) {
        imageName = imageCell.imageFile.name; // Only the original file name
      }

      // Create product entry in the exact format the API expects
      const productEntry = {
        tag_no: rowData.tagNo ? String(rowData.tagNo).trim() : "",
        product_group_id: resolvedProductGroupId,
        Supplier: resolvedSupplierId,
        huid: rowData.huidNo ? String(rowData.huidNo).trim() : "",
        design: rowData.design || "",
        size: rowData.size || "",
        pcs: Number(rowData.pcs) || 0,
        GWT: Number(rowData.grWt) || 0,
        NWT: resolvedNwt,
        LBR: Number(rowData.lbrPercentage) || 0,
        LWT_vepari: Number(rowData.lsWtVepari) || 0,
        LWT_customer: Number(rowData.lsWtCustomer) || 0,
        LBR_type: rowData.lType || "",
        LBR_rate: Number(rowData.lbrRate) || 0,
        LBR_amt: lbrAmount,
        othamt: Number(rowData.othAmt) || 0,
        MRP: Number(rowData.mrp) || 0,
        image: imageName, // Only the file name
        image_file: imageCell?.imageFile || null,
        image_name: imageName, // Just like MyProfile.jsx
        charges: charges,
        lbrCalcMode: tableData[rowIndex]?.lbrCalcMode || null,
      };

      productEntries.push(productEntry);
    }

    // Check if we have any valid entries
    if (productEntries.length === 0) {
      alert(
        "No valid product entries to submit. Please complete at least one row with Product Group.",
      );
      return;
    }

    try {
      const branch_id = getSessionBranchId();

      const items = [];
      for (let i = 0; i < productEntries.length; i++) {
        const entry = productEntries[i];
        const charges = Array.isArray(entry.charges) ? entry.charges : [];

        const lbrPercent = Number(entry.LBR) || 0;
        const lbrRate = Number(entry.LBR_rate) || 0;

        // API expects dropdown values only for lbr_type: NWt, GWt, Pcs, Fixed
        const uiLbrTypeRaw = String(entry.LBR_type || "").trim();
        const uiLbrType =
          uiLbrTypeRaw === "NWt" ||
          uiLbrTypeRaw === "GWt" ||
          uiLbrTypeRaw === "Pcs" ||
          uiLbrTypeRaw === "Fixed"
            ? uiLbrTypeRaw
            : "";

        if (!uiLbrType) {
          toast.error(
            `Row ${i + 1}: Invalid Lbr Type. Please select NWt, GWt, Pcs, or Fixed.`,
          );
          return;
        }

        items.push({
          tag_no: entry.tag_no || "",
          product_group_id:
            entry.product_group_id != null &&
            Number.isFinite(Number(entry.product_group_id))
              ? Number(entry.product_group_id)
              : null,
          pcs: Number(entry.pcs) || 0,
          huid: entry.huid || "",
          design: entry.design || "",
          size: entry.size || "",
          supplier_id:
            entry.Supplier != null && Number.isFinite(Number(entry.Supplier))
              ? Number(entry.Supplier)
              : null,
          image: entry.image || "",
          image_file: entry.image_file || undefined,
          gwt: Number(entry.GWT) || 0,
          lwt_vepari: Number(entry.LWT_vepari) || 0,
          lwt_customer: Number(entry.LWT_customer) || 0,
          nwt: entry.NWT ?? 0,
          lbr_type: uiLbrType,
          lbr_percent: uiLbrType === "Fixed" ? null : lbrPercent || null,
          lbr_rate: Number(lbrRate) || 0,
          lbr_amt: Number(entry.LBR_amt) || 0,
          oth_amt: Number(entry.othamt) || 0,
          mrp: Number(entry.MRP) || 0,
          charges,
        });
      }

      const payload = { branch_id, items };
      console.log("[AddStock] createSingleStock payload", payload);

      const result = await createSingleStock(payload);

      if (!result?.success) {
        toast.error(
          result?.message ||
            "Failed to add products. Check untagged pool balance for the product group.",
        );
        return;
      }
      trackEvent("Inventory", "Update", getTenantName());
      toast.success("Products added successfully");
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        navigate("/stocktable");
      }, 2000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add products",
      );
    }
  };

  // Updated handleImageUpload function - exactly like profile code
  // Updated handleImageUpload - Only store the file, don't upload yet
  const handleImageUpload = async (field, rowIndex) => {
    // console.log("Field:", field);

    // Create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview and store the file in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image",
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field; // Store the actual file object
            imageCell.value = null; // Clear any previous uploaded image name
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Function to reset the form after successful submission
  const resetForm = () => {
    // Reset the table data to a single empty row
    const initialRowData = COLUMNS.map((col) => ({
      id: col.id,
      value: "",
      ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
    }));
    setTableData([{ id: 1, data: initialRowData }]);

    // Reset any active dropdowns or modals
    setActiveDropdown({ row: null, col: null, type: null });
    setOtherAmountModal({
      isOpen: false,
      rowIndex: null,
      entries: [],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        wtPP: "",
        wtTotal: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });

    // Set today's date
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);

    // Reset image modal if open
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });

    // Reset any input focus
    document.activeElement?.blur();

    // Reset form to top position
    window.scrollTo(0, 0);
  };

  const openHuidModal = (rowIndex) => {
    setHuidModal({ isOpen: true, rowIndex });
  };

  const closeHuidModal = () => {
    setHuidModal({ isOpen: false, rowIndex: null });
  };

  const handleHuidKeyDown = (e, rowIndex) => {
    if (e.key === "Enter") {
      const now = Date.now();
      if (
        lastHuidEnterPress.rowIndex === rowIndex &&
        now - lastHuidEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        e.preventDefault();
        openHuidModal(rowIndex);
        setLastHuidEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastHuidEnterPress({ time: now, rowIndex });
      }
    }
  };

  const renderHuidModal = () => {
    if (!huidModal.isOpen) return null;

    return (
      <Modal
        show={huidModal.isOpen}
        onHide={closeHuidModal}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>HUID Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">HUID Number</label>
            <textarea
              className={`form-control ${buttonClass}`}
              rows="3"
              style={{ resize: "vertical" }}
              value={
                tableData[huidModal.rowIndex]?.data.find(
                  (cell) => cell.id === "huidNo",
                )?.value || ""
              }
              onChange={(e) => {
                handleInputChange(
                  huidModal.rowIndex,
                  tableData[huidModal.rowIndex]?.data.findIndex(
                    (cell) => cell.id === "huidNo",
                  ),
                  e.target.value,
                );
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={closeHuidModal}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center flex-wrap gap-2 w-100">
              <h4 className="card-title mb-0">Add Stock</h4>
              <ProductGroupPoolBalance balance={activePgBalance} />
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-1">
              <div className="col-md-2">
                <input
                  type="date"
                  className={`form-control pe-4 ${buttonClass}`}
                  value={billDate}
                  onChange={handleDateChange}
                  required
                />
                <i className="bi bi-calendar-date position-absolute top-50 end-0 translate-middle-y me-3 text-secondary"></i>
              </div>
            </div>

            <div
              ref={tableContainerRef}
              className={`table-responsive border mt-3 ${cardClass}`}
              style={{
                maxHeight:
                  tableData.length > 6
                    ? `${40 + 6 * 38 + 38}px` // header + 6 data rows + totals row
                    : undefined,
                overflowY: tableData.length > 6 ? "auto" : "visible",
              }}
            >
              {/* No table-rounded here — its overflow:hidden breaks sticky header/total (same as Sales) */}
              <table
                className="table table-bordered table-hover table-sm mb-0 theme-header-table"
                style={{ width: "max-content", minWidth: `${tableMinWidth}px` }}
              >
                <colgroup>
                  {COLUMNS.map((column) => (
                    <col
                      key={column.id}
                      style={{ width: column.width, minWidth: column.width }}
                    />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          position: tableData.length > 6 ? "sticky" : undefined,
                          top: tableData.length > 6 ? 0 : undefined,
                          zIndex: tableData.length > 6 ? 5 : undefined,
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                              onMouseEnter={() =>
                                setHoveredNoRowIndex(rowIndex)
                              }
                              onMouseLeave={() => setHoveredNoRowIndex(null)}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center gap-1"
                                style={{ height: "38px" }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center"
                                  onClick={() => handleDuplicateRow(rowIndex)}
                                  title={`Duplicate row ${rowIndex + 1}`}
                                  aria-label={`Duplicate row ${rowIndex + 1}`}
                                  style={{
                                    width: "14px",
                                    height: "14px",
                                    color: "var(--theme-primary)",
                                    textDecoration: "none",
                                    opacity:
                                      hoveredNoRowIndex === rowIndex ? 1 : 0,
                                    pointerEvents:
                                      hoveredNoRowIndex === rowIndex
                                        ? "auto"
                                        : "none",
                                    transition: "opacity 0.15s ease-in-out",
                                  }}
                                >
                                  <Icon
                                    icon="solar:copy-line-duotone"
                                    width={12}
                                  />
                                </button>
                                <span style={{ fontSize: "0.85rem" }}>
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue,
                                    );
                                    // Open dropdown at min chars (productGroup 3+, supplier 2+)
                                    const columnType = getColumnType(colIndex);
                                    const trimmed = newValue.trim();
                                    const meetsMinLength =
                                      columnType === "productGroup"
                                        ? trimmed.length >= 3
                                        : columnType === "supplier"
                                          ? trimmed.length >= 2
                                          : true;

                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      }, 0);
                                    } else if (
                                      columnType === "productGroup" ||
                                      columnType === "supplier"
                                    ) {
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    const trimmed = String(
                                      cell.value || "",
                                    ).trim();
                                    if (columnType === "productGroup") {
                                      if (trimmed.length >= 3) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      }
                                    } else if (columnType === "supplier") {
                                      if (trimmed.length >= 2) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType,
                                        );
                                      }
                                    } else {
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType,
                                      );
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                    (fileInputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) =>
                                    handleImageUpload(
                                      e.target.files[0],
                                      rowIndex,
                                    )
                                  }
                                />

                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current,
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current,
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300,
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(cell.imagePreview);
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `tooltip-${rowIndex}-${colIndex}`
                                        ] = el)
                                      }
                                      className="shadow rounded p-3 bg-white"
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current,
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current,
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex,
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image",
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(cell.imagePreview);
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                    (e.target.style.backgroundColor =
                                      "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />
                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current,
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current,
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `oth-amt-tooltip-${rowIndex}`
                                        ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                  cell.otherAmountEntries
                                                    .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹
                                                {Number(entry.amount).toFixed(
                                                  2,
                                                )}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0,
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWtVepari" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value,
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "huidNo" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  readOnly
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex),
                                    );
                                    handleHuidKeyDown(e, rowIndex);
                                  }}
                                  onDoubleClick={(e) => {
                                    e.preventDefault();
                                    openHuidModal(rowIndex);
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                    caretColor: "transparent", // Hide the cursor
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  title="Double-click or press Enter twice to edit HUID details"
                                />
                                <button
                                  className={`btn btn-link btn-sm p-0 ms-1 ${buttonClass}`}
                                  onClick={() => openHuidModal(rowIndex)}
                                  title="Click to edit HUID details"
                                  style={{ color: "var(--theme-primary)" }}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                {cell.value && (
                                  <div className="position-relative">
                                    <button
                                      className={`btn btn-link btn-sm p-0 px-1 text-primary ${buttonClass}`}
                                      style={{ fontSize: "1rem", zIndex: 1 }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current,
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current,
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current,
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip,
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                      // title="Click for HUID information"
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </button>
                                    {/* HUID Info Tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `huid-tooltip-${rowIndex}`
                                        ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                        }}
                                      ></div>
                                      <div
                                        className="huid-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.value ? (
                                          cell.value
                                            .split(",")
                                            .map((huid, index, arr) => (
                                              <span
                                                key={index}
                                                style={{
                                                  fontSize: "0.85rem",
                                                  margin: 0,
                                                }}
                                              >
                                                {`${index + 1}. ${huid.trim()}`}
                                                {index < arr.length - 1 ? (
                                                  <br />
                                                ) : null}
                                              </span>
                                            ))
                                        ) : (
                                          <p
                                            style={{
                                              fontSize: "0.85rem",
                                              margin: 0,
                                            }}
                                          >
                                            No HUID entered
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <input
                                ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Text / alphanumeric fields (tag no, design, size, HUID, etc.)
                                  const textFields = [
                                    "tagNo",
                                    "design",
                                    "size",
                                    "huidNo",
                                    "lType",
                                  ];
                                  if (textFields.includes(columnType)) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value,
                                    );
                                  }
                                }}
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row — sticky on each td (same pattern as Sales) */}
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      position: tableData.length > 6 ? "sticky" : undefined,
                      bottom: tableData.length > 6 ? 0 : undefined,
                      zIndex: tableData.length > 6 ? 5 : undefined,
                    }}
                  >
                    {COLUMNS.map((column) => {
                      const stickyTd =
                        tableData.length > 6
                          ? {
                              position: "sticky",
                              bottom: 0,
                              zIndex: 5,
                            }
                          : {};
                      const isNumericTotal = [
                        "pcs",
                        "grWt",
                        "lsWtVepari",
                        "lsWtCustomer",
                        "netWt",
                        "lbrAmt",
                        "othAmt",
                        "totalAmt",
                        "mrp",
                      ].includes(column.id);

                      if (column.id === "no") {
                        return (
                          <td
                            key={column.id}
                            className="p-0"
                            style={{
                              width: column.width,
                              backgroundColor: "#f8f9fa",
                              ...stickyTd,
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                Total
                              </span>
                            </div>
                          </td>
                        );
                      }

                      if (!isNumericTotal) {
                        return (
                          <td
                            key={column.id}
                            className="p-0"
                            style={{
                              backgroundColor: "#f8f9fa",
                              ...stickyTd,
                            }}
                          />
                        );
                      }

                      const totalValue = tableData.reduce((sum, row) => {
                        const raw = parseFloat(
                          row.data.find((cell) => cell.id === column.id)
                            ?.value || 0,
                        );
                        return sum + (isNaN(raw) ? 0 : raw);
                      }, 0);
                      const display =
                        column.id === "pcs"
                          ? totalValue
                          : totalValue.toFixed(2);

                      return (
                        <td
                          key={column.id}
                          className="p-0"
                          style={{
                            backgroundColor: "#fff",
                            ...stickyTd,
                          }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-end px-2"
                            style={{ height: "38px" }}
                          >
                            <span
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: "600",
                              }}
                            >
                              {display}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            {canAddProduct && (
              <div className="text-end mt-3">
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  onClick={handleSubmit}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderImageModal()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        applyChargeType={(entry, chargeName) =>
          applyOtherChargeToEntry(entry, chargeTypes, chargeName)
        }
        chargeOptions={chargeTypes}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={rateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={calculateTotal}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableData}
      />
      {renderHuidModal()}
    </div>
  );
};

export default AddStock;
